import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Users, Save, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import EmailShareDialog from "@/components/EmailShareDialog";
import { useLang } from "@/context/LanguageContext";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/api";

/* ------------------------------------------------------------------ */
/* Option lists (keys stored in DB — labels resolved per language)     */
/* ------------------------------------------------------------------ */
const MARITAL = ["celibataire", "divorce", "pacse", "veuf", "concubinage", "marie"];
const CONTRACTS = ["communaute_reduite", "communaute_universelle", "participation_acquets", "separation_biens"];
const TIMESCALES = ["3_months", "3_months_plus"];
const STATUTS = ["pleine_propriete", "indivision"];
const STANDINGS = ["luxe", "standing", "bon_standing", "propre", "a_renover"];
const PARKINGS = ["parking_libre", "garage"];
const SECURITY = ["digicode", "interphone", "video", "videophone", "gardien"];
const TYPES_BIEN = ["atelier", "bastide", "californienne", "classique", "contemporain", "duplex", "ferme", "loft",
  "maison_architecte", "maison_campagne", "maison_ville", "maison_provencale", "maison_rustique", "plateau_brut"];
const ENVIRONNEMENTS = ["campagne", "centre_ville", "pavillonnaire", "residentiel", "zone_commerciale"];
const TYPES_T = ["studio", "t1", "t2", "t3", "t4", "t5", "t6", "t6_plus"];
const LINER_STATES = ["bon_etat", "a_changer"];
const POOL_TREATMENTS = ["brome", "chlore", "sel", "ionisation"];
const SOLAR_MODES = ["production_seule", "auto_utilisation", "production_auto"];
const ASSAINISSEMENTS = ["tout_egout", "fosse_septique", "micro_station"];
const CHAUFFAGES = ["gaz", "fioul", "solaire", "bois", "clim_reversible", "electrique", "geothermie", "pompe_chaleur", "gainable"];
const DIFFUSIONS = ["radiateur", "sol"];
const EAUX_CHAUDES = ["cumulus", "chaudiere", "pompe_chaleur"];
const MENUISERIES = ["double_vitrage", "simple_vitrage", "survitrage", "bois", "aluminium", "pvc"];
const VOLETS = ["bois", "aluminium", "pvc", "manuels", "electriques"];
const PORTAILS = ["ouverture_simple", "ouverture_automatisee"];

/* ------------------------------------------------------------------ */
/* Local bilingual dictionary (kept here to avoid bloating the global  */
/* LanguageContext with ~150 dossier-only keys)                        */
/* ------------------------------------------------------------------ */
const L = {
  en: {
    subtitle: "Seller technical information sheet — everything gathered at the estimation appointment.",
    date: "Date", consultant: "Consultant",
    generalSection: "General", vendorsSection: "Vendors", projectSection: "Property project",
    bienSection: "The property", techSection: "Technical details",
    vendor: "Vendor", addVendor: "Add vendor", prefill: "Pre-fill from linked seller(s)",
    noLinkedSellers: "No sellers are linked to this property.", prefilled: "Vendor details pre-filled",
    fullName: "Full name", address: "Address", profession: "Occupation",
    dateOfBirth: "Date of birth", birthPlace: "Town of birth", postcodeOfBirth: "Postcode of birth",
    countryOfBirth: "Country of birth", nationality: "Nationality",
    phone: "Phone", email: "Email", maritalStatus: "Marital status", marriageDate: "Date of marriage",
    marriageContract: "Marriage contract",
    reasons: "Reasons for selling", purchaseDate: "When was the property bought?",
    remainingAmount: "Amount left to pay (€)", bridgingLoan: "Bridging loan (prêt relais)?",
    bridgingLoanUntil: "If yes, until what date?", alreadyOnMarket: "If already on the market",
    onMarketSince: "On the market since", hadVisits: "Any viewings?", visitCount: "How many?",
    currentPrice: "Current asking price (€)", otherAgencies: "Other agencies?", agencyCount: "How many?",
    idealTimescale: "Ideal timescale to sell", planIfNotSold: "If not sold within your timescale, what will you do?",
    bienConcerne: "Property concerned", taxeFonciere: "Taxe foncière (€)", taxeHabitation: "Taxe d'habitation (€)",
    chargesMensuelles: "Monthly charges (€)", cadastralRef: "Cadastral reference",
    copro: "Co-ownership (copropriété)?", nbLots: "Number of lots", lotissementName: "Housing estate (lotissement) name",
    syndicName: "Syndic name", syndicPhone: "Syndic phone", chargesCopro: "Annual co-ownership charges (€)",
    lotNumbers: "Lot number(s)", statutJuridique: "Legal status", standing: "Condition / standing",
    parking: "Parking", parkingNumber: "Parking space no.", garageNumber: "Garage no.",
    security: "Security", ascenseur: "Lift?",
    bienLoue: "Property rented?", tenantName: "Tenant name", tenantPhone: "Tenant phone", tenantEmail: "Tenant email",
    bailDate: "Lease date", loyerHC: "Rent excl. charges (€)", chargesLocatives: "Rental charges (€)",
    congeVente: "Notice to sell (congé pour vente)?",
    typeDeBien: "Property style", environnement: "Surroundings", typeT: "Type",
    piscine: "Pool?", liner: "Liner?", linerState: "Liner condition",
    poolWidth: "Width (m)", poolLength: "Length (m)", poolDepth: "Depth (m)", poolTreatment: "Treatment",
    plainPied: "Single storey?", nbNiveaux: "Number of levels", nbChambresRdc: "Ground-floor bedrooms",
    dependances: "Outbuilding(s)?", panneauxSolaires: "Solar panels?", solarMode: "Solar use",
    rendementAnnuel: "Annual yield", assainissement: "Drainage", conformite: "Compliant?",
    chauffage: "Heating", diffusion: "Heat distribution", eauChaude: "Hot water",
    menuiseries: "Window frames", volets: "Shutters", portail: "Gate",
    printDossier: "Print / PDF", saveDossier: "Save dossier", lastSaved: "Last saved",
    roomSchedule: "Room measurements", yes: "Yes", no: "No",
    dossierTitle: "Estimation dossier",
  },
  fr: {
    subtitle: "Fiche technique vendeur — toutes les informations recueillies au rendez-vous d'estimation.",
    date: "Date", consultant: "Consultant",
    generalSection: "Général", vendorsSection: "Vendeurs", projectSection: "Projet immobilier",
    bienSection: "Le bien", techSection: "Fiche technique",
    vendor: "Vendeur", addVendor: "Ajouter un vendeur", prefill: "Pré-remplir depuis le(s) vendeur(s) lié(s)",
    noLinkedSellers: "Aucun vendeur n'est lié à ce bien.", prefilled: "Coordonnées vendeur pré-remplies",
    fullName: "Nom - Prénom", address: "Adresse", profession: "Profession",
    dateOfBirth: "Date de naissance", birthPlace: "Ville de naissance", postcodeOfBirth: "Code postal de naissance",
    countryOfBirth: "Pays de naissance", nationality: "Nationalité",
    phone: "Tél", email: "Email", maritalStatus: "Situation familiale", marriageDate: "Date du mariage",
    marriageContract: "Quel contrat",
    reasons: "Raisons de la mise en vente", purchaseDate: "Quand avez-vous acheté ce bien ?",
    remainingAmount: "Montant restant à payer (€)", bridgingLoan: "Avez-vous un prêt relais ?",
    bridgingLoanUntil: "Si oui, jusqu'à quelle date ?", alreadyOnMarket: "Si le bien est déjà en vente",
    onMarketSince: "En vente depuis", hadVisits: "Avez-vous eu des visites ?", visitCount: "Combien ?",
    currentPrice: "Prix de vente actuel (€)", otherAgencies: "Avez-vous d'autres agences ?", agencyCount: "Combien ?",
    idealTimescale: "Délai de vente idéal", planIfNotSold: "Si le bien n'est pas vendu dans votre délai, que comptez-vous faire ?",
    bienConcerne: "Bien concerné", taxeFonciere: "Taxe foncière (€)", taxeHabitation: "Taxe d'habitation (€)",
    chargesMensuelles: "Charges mensuelles (€)", cadastralRef: "Référence cadastrale",
    copro: "Le bien est-il en copropriété ?", nbLots: "Nombre de lots", lotissementName: "Si lotissement, nom",
    syndicName: "Nom du syndic", syndicPhone: "Téléphone du syndic", chargesCopro: "Charges de copropriété annuelles (€)",
    lotNumbers: "Numéro de lots", statutJuridique: "Statut juridique", standing: "État / standing",
    parking: "Stationnement", parkingNumber: "N° place de parking", garageNumber: "N° garage",
    security: "Sécurité", ascenseur: "Ascenseur ?",
    bienLoue: "Bien loué ?", tenantName: "Nom du locataire", tenantPhone: "Téléphone du locataire", tenantEmail: "Email du locataire",
    bailDate: "Date du bail", loyerHC: "Loyer hors charges (€)", chargesLocatives: "Charges locatives (€)",
    congeVente: "Congé pour vente ?",
    typeDeBien: "Type de bien", environnement: "Environnement du bien", typeT: "Type",
    piscine: "Piscine ?", liner: "Liner ?", linerState: "État du liner",
    poolWidth: "Largeur (m)", poolLength: "Longueur (m)", poolDepth: "Profondeur (m)", poolTreatment: "Traitement",
    plainPied: "Plain-pied ?", nbNiveaux: "Nombre de niveaux", nbChambresRdc: "Nombre de chambres au RDC",
    dependances: "Dépendance(s) ?", panneauxSolaires: "Panneaux solaires ?", solarMode: "Utilisation solaire",
    rendementAnnuel: "Rendement annuel", assainissement: "Assainissement", conformite: "Conformité ?",
    chauffage: "Chauffage", diffusion: "Diffusion de la chaleur", eauChaude: "Eau chaude",
    menuiseries: "Menuiseries", volets: "Volets", portail: "Portail",
    printDossier: "Imprimer / PDF", saveDossier: "Enregistrer le dossier", lastSaved: "Dernier enregistrement",
    roomSchedule: "Mesurage des pièces", yes: "Oui", no: "Non",
    dossierTitle: "Dossier d'estimation",
  },
};

const OPT = {
  en: {
    celibataire: "Single", divorce: "Divorced", pacse: "PACS", veuf: "Widowed", concubinage: "Cohabiting", marie: "Married",
    communaute_reduite: "Communauté réduite aux acquêts", communaute_universelle: "Communauté universelle",
    participation_acquets: "Participation aux acquêts", separation_biens: "Séparation de biens",
    "3_months": "3 months", "3_months_plus": "+ 3 months",
    pleine_propriete: "Full ownership", indivision: "Joint ownership (indivision)",
    luxe: "Luxury", standing: "High standing", bon_standing: "Good standing", propre: "Clean", a_renover: "To renovate",
    parking_libre: "Open parking", garage: "Garage",
    digicode: "Door code", interphone: "Intercom", video: "Video", videophone: "Videophone", gardien: "Caretaker",
    atelier: "Workshop", bastide: "Bastide", californienne: "Californian", classique: "Classic",
    contemporain: "Contemporary", duplex: "Duplex", ferme: "Farmhouse", loft: "Loft",
    maison_architecte: "Architect-designed", maison_campagne: "Country house", maison_ville: "Town house",
    maison_provencale: "Provençal house", maison_rustique: "Rustic house", plateau_brut: "Unfinished shell",
    campagne: "Countryside", centre_ville: "Town centre", pavillonnaire: "Suburban",
    residentiel: "Residential", zone_commerciale: "Commercial area",
    studio: "Studio", t1: "T1", t2: "T2", t3: "T3", t4: "T4", t5: "T5", t6: "T6", t6_plus: "T6 +",
    bon_etat: "Good condition", a_changer: "To be replaced",
    brome: "Bromine", chlore: "Chlorine", sel: "Salt", ionisation: "Ionisation",
    production_seule: "Production only", auto_utilisation: "Self-consumption", production_auto: "Production + self-consumption",
    tout_egout: "Mains drainage", fosse_septique: "Septic tank", micro_station: "Micro treatment plant",
    gaz: "Gas", fioul: "Oil", solaire: "Solar", bois: "Wood", clim_reversible: "Reversible A/C",
    electrique: "Electric", geothermie: "Geothermal", pompe_chaleur: "Heat pump", gainable: "Ducted",
    radiateur: "Radiators", sol: "Underfloor",
    cumulus: "Hot water tank", chaudiere: "Boiler",
    double_vitrage: "Double glazing", simple_vitrage: "Single glazing", survitrage: "Secondary glazing",
    aluminium: "Aluminium", pvc: "PVC", manuels: "Manual", electriques: "Electric",
    ouverture_simple: "Manual opening", ouverture_automatisee: "Automated opening",
  },
  fr: {
    celibataire: "Célibataire", divorce: "Divorcé(e)", pacse: "Pacsé(e)", veuf: "Veuf(ve)", concubinage: "Concubinage", marie: "Marié(e)",
    communaute_reduite: "Communauté réduite aux acquêts", communaute_universelle: "Communauté universelle",
    participation_acquets: "Participation aux acquêts", separation_biens: "Séparation de biens",
    "3_months": "3 mois", "3_months_plus": "+ 3 mois",
    pleine_propriete: "Pleine propriété", indivision: "Indivision",
    luxe: "Luxe", standing: "Standing", bon_standing: "Bon standing", propre: "Propre", a_renover: "À rénover",
    parking_libre: "Parking libre", garage: "Garage",
    digicode: "Digicode", interphone: "Interphone", video: "Vidéo", videophone: "Vidéophone", gardien: "Gardien",
    atelier: "Atelier", bastide: "Bastide", californienne: "Californienne", classique: "Classique",
    contemporain: "Contemporain", duplex: "Duplex", ferme: "Ferme", loft: "Loft",
    maison_architecte: "Maison d'architecte", maison_campagne: "Maison de campagne", maison_ville: "Maison de ville",
    maison_provencale: "Maison provençale", maison_rustique: "Maison rustique", plateau_brut: "Plateau brut",
    campagne: "Campagne", centre_ville: "Centre-ville", pavillonnaire: "Pavillonnaire",
    residentiel: "Résidentiel", zone_commerciale: "Zone commerciale",
    studio: "Studio", t1: "T1", t2: "T2", t3: "T3", t4: "T4", t5: "T5", t6: "T6", t6_plus: "T6 +",
    bon_etat: "Bon état", a_changer: "À changer",
    brome: "Brome", chlore: "Chlore", sel: "Sel", ionisation: "Ionisation",
    production_seule: "Production seule", auto_utilisation: "Auto-utilisation", production_auto: "Production + auto-utilisation",
    tout_egout: "Tout à l'égout", fosse_septique: "Fosse septique", micro_station: "Micro-station",
    gaz: "Gaz", fioul: "Fioul", solaire: "Solaire", bois: "Bois", clim_reversible: "Clim. réversible",
    electrique: "Électrique", geothermie: "Géothermie", pompe_chaleur: "Pompe à chaleur", gainable: "Gainable",
    radiateur: "Radiateurs", sol: "Sol",
    cumulus: "Cumulus", chaudiere: "Chaudière",
    double_vitrage: "Double vitrage", simple_vitrage: "Simple vitrage", survitrage: "Survitrage",
    aluminium: "Aluminium", pvc: "PVC", manuels: "Manuels", electriques: "Électriques",
    ouverture_simple: "Ouverture simple", ouverture_automatisee: "Ouverture automatisée",
  },
};

const YES_NO = ["oui", "non"];

const emptyVendor = () => ({
  _uid: crypto.randomUUID(),
  name: "", address: "", profession: "", phone: "", email: "",
  date_of_birth: "", birth_place: "", birth_postcode: "", country_of_birth: "", nationality: "",
  marital_status: "", marriage_date: "", marriage_contract: "",
});

const emptyDossier = (propertyId) => ({
  property_id: propertyId,
  date: new Date().toISOString().slice(0, 10),
  consultant: "",
  vendors: [emptyVendor()],
  project: {},
  bien: {},
  technical: {},
});

/* ------------------------------------------------------------------ */
/* Small form primitives                                               */
/* ------------------------------------------------------------------ */
const F = ({ label, children, className = "" }) => (
  <div className={`min-w-0 ${className}`}>
    <p className="text-xs font-medium text-stone-500 mb-1">{label}</p>
    {children}
  </div>
);

function Chips({ options, value, onChange, multi = false, opt, testId }) {
  const isOn = (o) => (multi ? (value || []).includes(o) : value === o);
  const toggle = (o) => {
    if (multi) {
      const cur = value || [];
      onChange(cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]);
    } else {
      onChange(value === o ? "" : o);
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o} type="button" onClick={() => toggle(o)}
          className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${isOn(o)
            ? "bg-stone-900 text-white border-stone-900"
            : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"}`}
        >
          {opt(o)}
        </button>
      ))}
    </div>
  );
}

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */
export default function PropertyDossier({ propertyId, propertyName }) {
  const { t, lang } = useLang();
  const tr = (k) => L[lang]?.[k] ?? L.en[k] ?? k;
  const opt = (k) => OPT[lang]?.[k] ?? OPT.en[k] ?? k;
  const ynLabel = (o) => (o === "oui" ? tr("yes") : tr("no"));

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    const { data } = await api.get(`/estimation_dossiers?property_id=${propertyId}`);
    if (data.length) {
      const d = data[0];
      setForm({
        ...emptyDossier(propertyId), ...d,
        vendors: (d.vendors?.length ? d.vendors : [emptyVendor()])
          .map((v) => (v._uid ? v : { ...v, _uid: crypto.randomUUID() })),
        project: d.project || {}, bien: d.bien || {}, technical: d.technical || {},
      });
    } else {
      setForm(emptyDossier(propertyId));
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const setTop = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setP = (k, v) => setForm((f) => ({ ...f, project: { ...f.project, [k]: v } }));
  const setB = (k, v) => setForm((f) => ({ ...f, bien: { ...f.bien, [k]: v } }));
  const setT = (k, v) => setForm((f) => ({ ...f, technical: { ...f.technical, [k]: v } }));
  const setVendor = (i, k, v) => setForm((f) => {
    const vendors = [...f.vendors];
    vendors[i] = { ...vendors[i], [k]: v };
    return { ...f, vendors };
  });

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, property_id: propertyId, property_name: propertyName || "" };
      const res = form.id
        ? await api.put(`/estimation_dossiers/${form.id}`, body)
        : await api.post("/estimation_dossiers", body);
      setForm((f) => ({ ...f, id: res.data.id, updated_at: res.data.updated_at }));
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const prefill = async () => {
    try {
      const { data } = await api.get("/sellers");
      const linked = data.filter((s) => s.property_id === propertyId);
      if (!linked.length) { toast.info(tr("noLinkedSellers")); return; }
      setForm((f) => {
        const vendors = [...(f.vendors || [])];
        linked.forEach((s, i) => {
          const cur = vendors[i] || emptyVendor();
          vendors[i] = {
            ...cur,
            name: cur.name || s.name || "",
            address: cur.address || s.address || "",
            phone: cur.phone || s.phone || "",
            email: cur.email || s.email || "",
            date_of_birth: cur.date_of_birth || s.date_of_birth || "",
            birth_place: cur.birth_place || s.place_of_birth || "",
            country_of_birth: cur.country_of_birth || s.country_of_birth || "",
            nationality: cur.nationality || s.nationality || "",
          };
        });
        return { ...f, vendors };
      });
      toast.success(tr("prefilled"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  /* ------------------------ Print / PDF & Email ------------------------ */
  const fetchRooms = async () => {
    try { return (await api.get(`/rooms?property_id=${propertyId}`)).data; }
    catch (err) {
      console.warn("Dossier: could not load rooms, continuing without room schedule", err);
      return [];
    }
  };

  const buildDossierHtml = (rooms, withPrintScript) => {
    const yn = (v) => (v === "oui" ? tr("yes") : v === "non" ? tr("no") : "—");
    const one = (v) => (v ? esc(opt(v)) : "—");
    const many = (arr) => (arr?.length ? esc(arr.map(opt).join(", ")) : "—");
    const txt = (v) => (v !== undefined && v !== null && v !== "" ? esc(v) : "—");
    const dat = (v) => (v ? esc(fmtDate(v, lang)) : "—");

    const rowsHtml = (rows) => rows.map(([l, v]) => `<tr><td class="l">${esc(l)}</td><td class="v">${v}</td></tr>`).join("");
    const secHtml = (title, rows) => `<section><h2>${esc(title)}</h2><table>${rowsHtml(rows)}</table></section>`;

    const p = form.project || {}, b = form.bien || {}, te = form.technical || {};

    const vendorSecs = (form.vendors || [])
      .filter((v) => Object.values(v).some((x) => x))
      .map((v, i) => secHtml(`${tr("vendor")} n°${i + 1}`, [
        [tr("fullName"), txt(v.name)], [tr("address"), txt(v.address)],
        [tr("profession"), txt(v.profession)],
        [tr("dateOfBirth"), dat(v.date_of_birth)], [tr("birthPlace"), txt(v.birth_place)],
        [tr("postcodeOfBirth"), txt(v.birth_postcode)], [tr("countryOfBirth"), txt(v.country_of_birth)],
        [tr("nationality"), txt(v.nationality)],
        [tr("phone"), txt(v.phone)], [tr("email"), txt(v.email)],
        [tr("maritalStatus"), one(v.marital_status)],
        [tr("marriageDate"), dat(v.marriage_date)],
        [tr("marriageContract"), one(v.marriage_contract)],
      ])).join("");

    const projectSec = secHtml(tr("projectSection"), [
      [tr("reasons"), txt(p.reasons)], [tr("purchaseDate"), txt(p.purchase_date)],
      [tr("remainingAmount"), txt(p.remaining_amount)],
      [tr("bridgingLoan"), yn(p.bridging_loan)], [tr("bridgingLoanUntil"), dat(p.bridging_loan_until)],
      [tr("onMarketSince"), txt(p.on_market_since)], [tr("hadVisits"), yn(p.had_visits)],
      [`${tr("hadVisits")} — ${tr("visitCount")}`, txt(p.visit_count)],
      [tr("currentPrice"), txt(p.current_price)], [tr("otherAgencies"), yn(p.other_agencies)],
      [`${tr("otherAgencies")} — ${tr("agencyCount")}`, txt(p.agency_count)],
      [tr("idealTimescale"), one(p.ideal_timescale)], [tr("planIfNotSold"), txt(p.plan_if_not_sold)],
    ]);

    const bienSec = secHtml(tr("bienSection"), [
      [tr("bienConcerne"), txt(b.bien_concerne)], [tr("address"), txt(b.address)],
      [tr("taxeFonciere"), txt(b.taxe_fonciere)], [tr("taxeHabitation"), txt(b.taxe_habitation)],
      [tr("chargesMensuelles"), txt(b.charges_mensuelles)], [tr("cadastralRef"), txt(b.cadastral_ref)],
      [tr("copro"), yn(b.copropriete)], [tr("nbLots"), txt(b.nb_lots)],
      [tr("lotissementName"), txt(b.lotissement_name)], [tr("syndicName"), txt(b.syndic_name)],
      [tr("syndicPhone"), txt(b.syndic_phone)], [tr("chargesCopro"), txt(b.charges_copro)],
      [tr("lotNumbers"), txt(b.lot_numbers)], [tr("statutJuridique"), one(b.statut_juridique)],
      [tr("standing"), one(b.standing)], [tr("parking"), many(b.parking)],
      [tr("parkingNumber"), txt(b.parking_number)], [tr("garageNumber"), txt(b.garage_number)],
      [tr("security"), many(b.security)], [tr("ascenseur"), yn(b.ascenseur)],
      [tr("bienLoue"), yn(b.bien_loue)], [tr("tenantName"), txt(b.tenant_name)],
      [tr("tenantPhone"), txt(b.tenant_phone)], [tr("tenantEmail"), txt(b.tenant_email)],
      [tr("bailDate"), dat(b.bail_date)], [tr("loyerHC"), txt(b.loyer_hc)],
      [tr("chargesLocatives"), txt(b.charges_locatives)], [tr("congeVente"), yn(b.conge_vente)],
    ]);

    const techSec = secHtml(tr("techSection"), [
      [tr("typeDeBien"), many(te.type_de_bien)], [tr("environnement"), many(te.environnement)],
      [tr("typeT"), one(te.type_t)],
      [tr("piscine"), yn(te.piscine)], [tr("liner"), yn(te.liner)],
      [tr("linerState"), one(te.liner_state)],
      [`${tr("piscine")} — ${tr("poolLength")} × ${tr("poolWidth")} × ${tr("poolDepth")}`,
        (te.pool_length || te.pool_width || te.pool_depth)
          ? esc(`${te.pool_length || "—"} × ${te.pool_width || "—"} × ${te.pool_depth || "—"}`) : "—"],
      [tr("poolTreatment"), one(te.pool_treatment)],
      [tr("plainPied"), yn(te.plain_pied)], [tr("nbNiveaux"), txt(te.nb_niveaux)],
      [tr("nbChambresRdc"), txt(te.nb_chambres_rdc)], [tr("dependances"), yn(te.dependances)],
      [tr("panneauxSolaires"), yn(te.panneaux_solaires)], [tr("solarMode"), one(te.solar_mode)],
      [tr("rendementAnnuel"), txt(te.rendement_annuel)],
      [tr("assainissement"), one(te.assainissement)], [tr("conformite"), yn(te.conformite)],
      [tr("chauffage"), many(te.chauffage)], [tr("diffusion"), many(te.diffusion)],
      [tr("eauChaude"), many(te.eau_chaude)], [tr("menuiseries"), many(te.menuiseries)],
      [tr("volets"), many(te.volets)], [tr("portail"), one(te.portail)],
    ]);

    /* Room schedule appended at the end (same grouping as the Rooms tab) */
    let roomsSec = "";
    if (rooms.length) {
      const buckets = {};
      rooms.forEach((r) => {
        const c = r.category || (r.exclude_habitable ? "other" : "habitable");
        (buckets[c] = buckets[c] || []).push(r);
      });
      const habitableTotal = rooms.reduce((s, r) => s + (r.exclude_habitable ? 0 : r.area || 0), 0);
      const excludedTotal = rooms.reduce((s, r) => s + (r.exclude_habitable ? r.area || 0 : 0), 0);
      const groups = Object.entries(buckets).map(([cat, list]) => {
        const items = list.map((r) => {
          const dims = (r.length && r.width) ? `${r.length} × ${r.width} ${r.area_unit || "m"}` : "";
          return `<tr><td class="l">${esc(r.name)}${r.exclude_habitable ? ` <span class="excl">${esc(t("excludedFromHabitable"))}</span>` : ""}</td><td class="dims">${esc(dims)}</td><td class="area">${(r.area ?? 0).toFixed(2)} ${r.area_unit || "m²"}</td></tr>`;
        }).join("");
        return `<h3>${esc(t(`roomCategory_${cat}`))}</h3><table>${items}</table>`;
      }).join("");
      roomsSec = `<section class="page-break"><h2>${esc(tr("roomSchedule"))}</h2>${groups}
        <div class="totals">
          <div><p>${esc(t("totalHabitable"))}</p><p>${habitableTotal.toFixed(2)} m²</p></div>
          ${excludedTotal > 0 ? `<div class="muted"><p>${esc(t("totalExcluded"))}</p><p>${excludedTotal.toFixed(2)} m²</p></div>` : ""}
          <div><p>${esc(t("totalArea"))}</p><p>${(habitableTotal + excludedTotal).toFixed(2)} m²</p></div>
        </div></section>`;
    }

    const html = `<!doctype html><html><head><meta charset="utf-8">      <title>${esc(tr("dossierTitle"))} — ${esc(propertyName || "")}</title>
      <style>
        body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #1c1917; margin: 0; }
        h1 { font-size: 20pt; margin: 0 0 2px; font-weight: 500; }
        .meta { font-size: 10pt; color: #78716c; margin-bottom: 20px; }
        section { margin-bottom: 18px; }
        h2 { font-size: 13pt; margin: 0 0 6px; font-weight: 600; border-bottom: 2px solid #1c1917; padding-bottom: 3px; }
        h3 { font-size: 10.5pt; margin: 10px 0 4px; font-weight: 600; color: #44403c; }
        table { width: 100%; border-collapse: collapse; border: 1px solid #e7e5e4; }
        td { padding: 4px 8px; font-size: 9.5pt; border-bottom: 1px solid #f5f5f4; vertical-align: top; }
        tr:last-child td { border-bottom: none; }
        td.l { width: 42%; color: #57534e; }
        td.v { font-weight: 500; }
        td.dims { color: #78716c; font-size: 9pt; white-space: nowrap; }
        td.area { text-align: right; font-weight: 500; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .excl { display: inline-block; margin-left: 5px; padding: 0 5px; background: #fef3c7; color: #92400e; font-size: 7.5pt; border-radius: 3px; text-transform: uppercase; }
        .totals { margin-top: 16px; padding-top: 10px; border-top: 2px solid #1c1917; display: flex; gap: 36px; }
        .totals div p:first-child { font-size: 8.5pt; color: #78716c; text-transform: uppercase; letter-spacing: 0.08em; margin: 0 0 2px; }
        .totals div p:last-child { font-size: 16pt; margin: 0; font-weight: 500; font-variant-numeric: tabular-nums; }
        .muted p:last-child { color: #78716c; }
        .page-break { page-break-before: always; }
        @page { margin: 14mm; }
      </style></head><body>
      <h1>${esc(tr("dossierTitle"))}${propertyName ? " — " + esc(propertyName) : ""}</h1>
      <p class="meta">${form.date ? esc(fmtDate(form.date, lang)) : ""}${form.consultant ? " · " + esc(tr("consultant")) + " : " + esc(form.consultant) : ""}</p>
      ${vendorSecs}
      ${projectSec}
      ${bienSec}
      ${techSec}
      ${roomsSec}
      ${withPrintScript ? '<script>window.onload = () => { setTimeout(() => window.print(), 200); };</' + 'script>' : ""}
    </body></html>`;
    return html;
  };

  const openPrint = async () => {
    const win = window.open("", "_blank", "width=800,height=1000");
    if (!win) { toast.error(t("popupBlocked")); return; }
    const rooms = await fetchRooms();
    const url = URL.createObjectURL(new Blob([buildDossierHtml(rooms, true)], { type: "text/html" }));
    win.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  if (!form) return null;
  const p = form.project, b = form.bien, te = form.technical;
  const grid2 = "grid grid-cols-1 sm:grid-cols-2 gap-3";

  return (
    <div data-testid="property-dossier" className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-stone-500 flex-1 min-w-[200px]">{tr("subtitle")}</p>
        <div className="flex gap-2">
          <Button data-testid="print-property-dossier" onClick={openPrint} variant="outline" className="border-stone-300">
            <Printer size={15} className="mr-1.5" /> {tr("printDossier")}
          </Button>
          <Button data-testid="email-property-dossier" onClick={() => setEmailOpen(true)} variant="outline" className="border-stone-300">
            <Mail size={15} className="mr-1.5" /> {t("emailFiles")}
          </Button>
          <Button data-testid="save-property-dossier" onClick={save} disabled={saving} className="bg-stone-900 hover:bg-stone-800 text-white">
            <Save size={15} className="mr-1.5" /> {tr("saveDossier")}
          </Button>
        </div>
      </div>

      <div className={grid2}>
        <F label={tr("date")}>
          <Input type="date" data-testid="dossier-date" value={form.date || ""} onChange={(e) => setTop("date", e.target.value)} />
        </F>
        <F label={tr("consultant")}>
          <Input data-testid="dossier-consultant" value={form.consultant || ""} onChange={(e) => setTop("consultant", e.target.value)} />
        </F>
      </div>

      <Accordion type="multiple" defaultValue={["vendors"]} className="space-y-2">
        {/* ------------------------- VENDORS ------------------------- */}
        <AccordionItem value="vendors" className="border border-stone-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid="dossier-section-vendors">
            {tr("vendorsSection")}
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <Button data-testid="dossier-prefill-vendors" onClick={prefill} variant="outline" size="sm" className="border-stone-300">
              <Users size={14} className="mr-1.5" /> {tr("prefill")}
            </Button>
            {form.vendors.map((v, i) => (
              <div key={v._uid} className="border border-stone-200 rounded-lg p-3 space-y-3" data-testid={`dossier-vendor-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{tr("vendor")} n°{i + 1}</p>
                  {form.vendors.length > 1 && (
                    <button data-testid={`dossier-vendor-remove-${i}`} onClick={() => setForm((f) => ({ ...f, vendors: f.vendors.filter((_, j) => j !== i) }))}
                      className="p-1 text-stone-300 hover:text-red-600 rounded transition-colors">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
                <div className={grid2}>
                  <F label={tr("fullName")}><Input data-testid={`dossier-vendor-name-${i}`} value={v.name || ""} onChange={(e) => setVendor(i, "name", e.target.value)} /></F>
                  <F label={tr("profession")}><Input value={v.profession || ""} onChange={(e) => setVendor(i, "profession", e.target.value)} /></F>
                  <F label={tr("address")} className="sm:col-span-2"><Input value={v.address || ""} onChange={(e) => setVendor(i, "address", e.target.value)} /></F>
                  <F label={tr("dateOfBirth")}><Input type="date" data-testid={`dossier-vendor-dob-${i}`} value={v.date_of_birth || ""} onChange={(e) => setVendor(i, "date_of_birth", e.target.value)} /></F>
                  <F label={tr("birthPlace")}><Input value={v.birth_place || ""} onChange={(e) => setVendor(i, "birth_place", e.target.value)} /></F>
                  <F label={tr("postcodeOfBirth")}><Input data-testid={`dossier-vendor-birth-postcode-${i}`} value={v.birth_postcode || ""} onChange={(e) => setVendor(i, "birth_postcode", e.target.value)} /></F>
                  <F label={tr("countryOfBirth")}><Input value={v.country_of_birth || ""} onChange={(e) => setVendor(i, "country_of_birth", e.target.value)} /></F>
                  <F label={tr("nationality")}><Input data-testid={`dossier-vendor-nationality-${i}`} value={v.nationality || ""} onChange={(e) => setVendor(i, "nationality", e.target.value)} /></F>
                  <F label={tr("phone")}><Input value={v.phone || ""} onChange={(e) => setVendor(i, "phone", e.target.value)} /></F>
                  <F label={tr("email")} className="sm:col-span-2"><Input type="email" value={v.email || ""} onChange={(e) => setVendor(i, "email", e.target.value)} /></F>
                </div>
                <F label={tr("maritalStatus")}>
                  <Chips options={MARITAL} value={v.marital_status} onChange={(val) => setVendor(i, "marital_status", val)} opt={opt} testId={`dossier-vendor-marital-${i}`} />
                </F>
                {v.marital_status === "marie" && (
                  <div className="space-y-3">
                    <F label={tr("marriageDate")}><Input type="date" value={v.marriage_date || ""} onChange={(e) => setVendor(i, "marriage_date", e.target.value)} /></F>
                    <F label={tr("marriageContract")}>
                      <Chips options={CONTRACTS} value={v.marriage_contract} onChange={(val) => setVendor(i, "marriage_contract", val)} opt={opt} />
                    </F>
                  </div>
                )}
              </div>
            ))}
            <Button data-testid="dossier-add-vendor" onClick={() => setForm((f) => ({ ...f, vendors: [...f.vendors, emptyVendor()] }))} variant="outline" size="sm" className="border-dashed border-stone-300 w-full">
              <Plus size={14} className="mr-1.5" /> {tr("addVendor")}
            </Button>
          </AccordionContent>
        </AccordionItem>

        {/* --------------------- PROJET IMMOBILIER --------------------- */}
        <AccordionItem value="project" className="border border-stone-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid="dossier-section-project">
            {tr("projectSection")}
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-4">
            <F label={tr("reasons")}>
              <textarea rows={2} data-testid="dossier-reasons" className="w-full text-sm border border-stone-200 rounded-lg p-2" value={p.reasons || ""} onChange={(e) => setP("reasons", e.target.value)} />
            </F>
            <div className={grid2}>
              <F label={tr("purchaseDate")}><Input value={p.purchase_date || ""} onChange={(e) => setP("purchase_date", e.target.value)} /></F>
              <F label={tr("remainingAmount")}><Input type="number" value={p.remaining_amount ?? ""} onChange={(e) => setP("remaining_amount", e.target.value)} /></F>
              <F label={tr("bridgingLoan")}><Chips options={YES_NO} value={p.bridging_loan} onChange={(v) => setP("bridging_loan", v)} opt={ynLabel} testId="dossier-bridging-loan" /></F>
              {p.bridging_loan === "oui" && (
                <F label={tr("bridgingLoanUntil")}><Input type="date" value={p.bridging_loan_until || ""} onChange={(e) => setP("bridging_loan_until", e.target.value)} /></F>
              )}
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-400 pt-1">{tr("alreadyOnMarket")}</p>
            <div className={grid2}>
              <F label={tr("onMarketSince")}><Input value={p.on_market_since || ""} onChange={(e) => setP("on_market_since", e.target.value)} /></F>
              <F label={tr("currentPrice")}><Input type="number" value={p.current_price ?? ""} onChange={(e) => setP("current_price", e.target.value)} /></F>
              <F label={tr("hadVisits")}><Chips options={YES_NO} value={p.had_visits} onChange={(v) => setP("had_visits", v)} opt={ynLabel} /></F>
              {p.had_visits === "oui" && (
                <F label={tr("visitCount")}><Input type="number" value={p.visit_count ?? ""} onChange={(e) => setP("visit_count", e.target.value)} /></F>
              )}
              <F label={tr("otherAgencies")}><Chips options={YES_NO} value={p.other_agencies} onChange={(v) => setP("other_agencies", v)} opt={ynLabel} /></F>
              {p.other_agencies === "oui" && (
                <F label={tr("agencyCount")}><Input type="number" value={p.agency_count ?? ""} onChange={(e) => setP("agency_count", e.target.value)} /></F>
              )}
            </div>
            <F label={tr("idealTimescale")}>
              <Chips options={TIMESCALES} value={p.ideal_timescale} onChange={(v) => setP("ideal_timescale", v)} opt={opt} testId="dossier-timescale" />
            </F>
            <F label={tr("planIfNotSold")}>
              <textarea rows={2} className="w-full text-sm border border-stone-200 rounded-lg p-2" value={p.plan_if_not_sold || ""} onChange={(e) => setP("plan_if_not_sold", e.target.value)} />
            </F>
          </AccordionContent>
        </AccordionItem>

        {/* -------------------------- LE BIEN -------------------------- */}
        <AccordionItem value="bien" className="border border-stone-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid="dossier-section-bien">
            {tr("bienSection")}
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-4">
            <div className={grid2}>
              <F label={tr("bienConcerne")}><Input data-testid="dossier-bien-concerne" value={b.bien_concerne || ""} onChange={(e) => setB("bien_concerne", e.target.value)} /></F>
              <F label={tr("address")}><Input value={b.address || ""} onChange={(e) => setB("address", e.target.value)} /></F>
              <F label={tr("taxeFonciere")}><Input type="number" value={b.taxe_fonciere ?? ""} onChange={(e) => setB("taxe_fonciere", e.target.value)} /></F>
              <F label={tr("taxeHabitation")}><Input type="number" value={b.taxe_habitation ?? ""} onChange={(e) => setB("taxe_habitation", e.target.value)} /></F>
              <F label={tr("chargesMensuelles")}><Input type="number" value={b.charges_mensuelles ?? ""} onChange={(e) => setB("charges_mensuelles", e.target.value)} /></F>
              <F label={tr("cadastralRef")}><Input value={b.cadastral_ref || ""} onChange={(e) => setB("cadastral_ref", e.target.value)} /></F>
            </div>
            <div className={grid2}>
              <F label={tr("copro")}><Chips options={YES_NO} value={b.copropriete} onChange={(v) => setB("copropriete", v)} opt={ynLabel} testId="dossier-copro" /></F>
              {b.copropriete === "oui" && (
                <>
                  <F label={tr("nbLots")}><Input type="number" value={b.nb_lots ?? ""} onChange={(e) => setB("nb_lots", e.target.value)} /></F>
                  <F label={tr("syndicName")}><Input value={b.syndic_name || ""} onChange={(e) => setB("syndic_name", e.target.value)} /></F>
                  <F label={tr("syndicPhone")}><Input value={b.syndic_phone || ""} onChange={(e) => setB("syndic_phone", e.target.value)} /></F>
                  <F label={tr("chargesCopro")}><Input type="number" value={b.charges_copro ?? ""} onChange={(e) => setB("charges_copro", e.target.value)} /></F>
                  <F label={tr("lotNumbers")}><Input value={b.lot_numbers || ""} onChange={(e) => setB("lot_numbers", e.target.value)} /></F>
                </>
              )}
              <F label={tr("lotissementName")}><Input value={b.lotissement_name || ""} onChange={(e) => setB("lotissement_name", e.target.value)} /></F>
            </div>
            <F label={tr("statutJuridique")}>
              <Chips options={STATUTS} value={b.statut_juridique} onChange={(v) => setB("statut_juridique", v)} opt={opt} testId="dossier-statut" />
            </F>
            <F label={tr("standing")}>
              <Chips options={STANDINGS} value={b.standing} onChange={(v) => setB("standing", v)} opt={opt} />
            </F>
            <F label={tr("parking")}>
              <Chips multi options={PARKINGS} value={b.parking} onChange={(v) => setB("parking", v)} opt={opt} />
            </F>
            <div className={grid2}>
              <F label={tr("parkingNumber")}><Input value={b.parking_number || ""} onChange={(e) => setB("parking_number", e.target.value)} /></F>
              <F label={tr("garageNumber")}><Input value={b.garage_number || ""} onChange={(e) => setB("garage_number", e.target.value)} /></F>
            </div>
            <F label={tr("security")}>
              <Chips multi options={SECURITY} value={b.security} onChange={(v) => setB("security", v)} opt={opt} />
            </F>
            <div className={grid2}>
              <F label={tr("ascenseur")}><Chips options={YES_NO} value={b.ascenseur} onChange={(v) => setB("ascenseur", v)} opt={ynLabel} /></F>
              <F label={tr("bienLoue")}><Chips options={YES_NO} value={b.bien_loue} onChange={(v) => setB("bien_loue", v)} opt={ynLabel} testId="dossier-bien-loue" /></F>
            </div>
            {b.bien_loue === "oui" && (
              <div className={grid2}>
                <F label={tr("tenantName")}><Input value={b.tenant_name || ""} onChange={(e) => setB("tenant_name", e.target.value)} /></F>
                <F label={tr("tenantPhone")}><Input value={b.tenant_phone || ""} onChange={(e) => setB("tenant_phone", e.target.value)} /></F>
                <F label={tr("tenantEmail")}><Input type="email" value={b.tenant_email || ""} onChange={(e) => setB("tenant_email", e.target.value)} /></F>
                <F label={tr("bailDate")}><Input type="date" value={b.bail_date || ""} onChange={(e) => setB("bail_date", e.target.value)} /></F>
                <F label={tr("loyerHC")}><Input type="number" value={b.loyer_hc ?? ""} onChange={(e) => setB("loyer_hc", e.target.value)} /></F>
                <F label={tr("chargesLocatives")}><Input type="number" value={b.charges_locatives ?? ""} onChange={(e) => setB("charges_locatives", e.target.value)} /></F>
                <F label={tr("congeVente")}><Chips options={YES_NO} value={b.conge_vente} onChange={(v) => setB("conge_vente", v)} opt={ynLabel} /></F>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* --------------------- FICHE TECHNIQUE --------------------- */}
        <AccordionItem value="technical" className="border border-stone-200 rounded-xl px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid="dossier-section-technical">
            {tr("techSection")}
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-4">
            <F label={tr("typeDeBien")}>
              <Chips multi options={TYPES_BIEN} value={te.type_de_bien} onChange={(v) => setT("type_de_bien", v)} opt={opt} testId="dossier-type-bien" />
            </F>
            <F label={tr("environnement")}>
              <Chips multi options={ENVIRONNEMENTS} value={te.environnement} onChange={(v) => setT("environnement", v)} opt={opt} />
            </F>
            <F label={tr("typeT")}>
              <Chips options={TYPES_T} value={te.type_t} onChange={(v) => setT("type_t", v)} opt={opt} testId="dossier-type-t" />
            </F>
            <div className={grid2}>
              <F label={tr("piscine")}><Chips options={YES_NO} value={te.piscine} onChange={(v) => setT("piscine", v)} opt={ynLabel} testId="dossier-piscine" /></F>
              {te.piscine === "oui" && (
                <F label={tr("liner")}><Chips options={YES_NO} value={te.liner} onChange={(v) => setT("liner", v)} opt={ynLabel} /></F>
              )}
            </div>
            {te.piscine === "oui" && (
              <>
                {te.liner === "oui" && (
                  <F label={tr("linerState")}>
                    <Chips options={LINER_STATES} value={te.liner_state} onChange={(v) => setT("liner_state", v)} opt={opt} />
                  </F>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <F label={tr("poolLength")}><Input type="number" step="0.01" value={te.pool_length ?? ""} onChange={(e) => setT("pool_length", e.target.value)} /></F>
                  <F label={tr("poolWidth")}><Input type="number" step="0.01" value={te.pool_width ?? ""} onChange={(e) => setT("pool_width", e.target.value)} /></F>
                  <F label={tr("poolDepth")}><Input type="number" step="0.01" value={te.pool_depth ?? ""} onChange={(e) => setT("pool_depth", e.target.value)} /></F>
                </div>
                <F label={tr("poolTreatment")}>
                  <Chips options={POOL_TREATMENTS} value={te.pool_treatment} onChange={(v) => setT("pool_treatment", v)} opt={opt} />
                </F>
              </>
            )}
            <div className={grid2}>
              <F label={tr("plainPied")}><Chips options={YES_NO} value={te.plain_pied} onChange={(v) => setT("plain_pied", v)} opt={ynLabel} /></F>
              <F label={tr("nbNiveaux")}><Input type="number" value={te.nb_niveaux ?? ""} onChange={(e) => setT("nb_niveaux", e.target.value)} /></F>
              <F label={tr("nbChambresRdc")}><Input type="number" value={te.nb_chambres_rdc ?? ""} onChange={(e) => setT("nb_chambres_rdc", e.target.value)} /></F>
              <F label={tr("dependances")}><Chips options={YES_NO} value={te.dependances} onChange={(v) => setT("dependances", v)} opt={ynLabel} /></F>
            </div>
            <div className={grid2}>
              <F label={tr("panneauxSolaires")}><Chips options={YES_NO} value={te.panneaux_solaires} onChange={(v) => setT("panneaux_solaires", v)} opt={ynLabel} testId="dossier-solaires" /></F>
              {te.panneaux_solaires === "oui" && (
                <F label={tr("rendementAnnuel")}><Input value={te.rendement_annuel || ""} onChange={(e) => setT("rendement_annuel", e.target.value)} /></F>
              )}
            </div>
            {te.panneaux_solaires === "oui" && (
              <F label={tr("solarMode")}>
                <Chips options={SOLAR_MODES} value={te.solar_mode} onChange={(v) => setT("solar_mode", v)} opt={opt} />
              </F>
            )}
            <div className={grid2}>
              <F label={tr("assainissement")}>
                <Chips options={ASSAINISSEMENTS} value={te.assainissement} onChange={(v) => setT("assainissement", v)} opt={opt} testId="dossier-assainissement" />
              </F>
              <F label={tr("conformite")}><Chips options={YES_NO} value={te.conformite} onChange={(v) => setT("conformite", v)} opt={ynLabel} /></F>
            </div>
            <F label={tr("chauffage")}>
              <Chips multi options={CHAUFFAGES} value={te.chauffage} onChange={(v) => setT("chauffage", v)} opt={opt} testId="dossier-chauffage" />
            </F>
            <F label={tr("diffusion")}>
              <Chips multi options={DIFFUSIONS} value={te.diffusion} onChange={(v) => setT("diffusion", v)} opt={opt} />
            </F>
            <F label={tr("eauChaude")}>
              <Chips multi options={EAUX_CHAUDES} value={te.eau_chaude} onChange={(v) => setT("eau_chaude", v)} opt={opt} />
            </F>
            <F label={tr("menuiseries")}>
              <Chips multi options={MENUISERIES} value={te.menuiseries} onChange={(v) => setT("menuiseries", v)} opt={opt} />
            </F>
            <F label={tr("volets")}>
              <Chips multi options={VOLETS} value={te.volets} onChange={(v) => setT("volets", v)} opt={opt} />
            </F>
            <F label={tr("portail")}>
              <Chips options={PORTAILS} value={te.portail} onChange={(v) => setT("portail", v)} opt={opt} />
            </F>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex items-center justify-between gap-3">
        {form.updated_at ? (
          <p className="text-[11px] text-stone-400">{tr("lastSaved")}: {fmtDate(form.updated_at.slice(0, 10), lang)}</p>
        ) : <span />}
        <Button data-testid="save-property-dossier-bottom" onClick={save} disabled={saving} className="bg-stone-900 hover:bg-stone-800 text-white">
          <Save size={15} className="mr-1.5" /> {tr("saveDossier")}
        </Button>
      </div>
      <EmailShareDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        contextName={propertyName || ""}
        defaultSubject={`${tr("dossierTitle")}${propertyName ? " — " + propertyName : ""}`}
        getBodyHtml={async () => buildDossierHtml(await fetchRooms(), false)}
      />
    </div>
  );
}
